import { FC, SubmitEventHandler } from 'react';
import { Box, LoadingOverlay } from '@mantine/core';
import { ProductPreview } from './productPreview/ProductPreview';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { Aside } from './aside/Aside';
import { DownloadDrawer } from './partials/downloadDrawer/DownloadDrawer';
import { CreatorHelpDrawer } from './partials/creatorHelpDrawer/CreatorHelpDrawer';
import { CreatorSummaryDrawer } from './partials/creatorSummaryDrawer/CreatorSummaryDrawer';
import { MessageDataTitle, SavePresetData, SubmitPayload } from '@/types/types';
import { creatorActions } from '@/store/slices/creatorSlice';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { RestoreProjectModal } from './partials/restoreProjectModal/RestoreProjectModal';
import { setLoading } from '@/store/slices/appSlice';
import APP_CONFIG from '@/configs/appConfig';
import ProjectApi from '@/api/ProjectApi';
import AppApi from '@/api/AppApi';

const renderer = ProjectRenderer.getInstance();

interface Props {
	handleClose: () => void;
}

export const Creator: FC<Props> = ({ handleClose }) => {
	const dispatch = useTypedDispatch();

	const isLoading = useTypedSelector((state) => state.app.loading);
	const { options, selectedProductModuleCollection, preset, project } =
		useTypedSelector((app) => app.creator);

	const handleSubmit: SubmitEventHandler<HTMLFormElement> = async (e) => {
		e.preventDefault();

		const serializedRenderer = renderer.export();

		if (options.isPresetMode) {
			const data: SavePresetData = {
				id: preset.id,
				collectionId: selectedProductModuleCollection!.id,
				renderer: serializedRenderer,
			};

			window!.top!.postMessage(
				{
					title: MessageDataTitle.SAVE_PRESET,
					data,
				},
				'*'
			);

			dispatch(creatorActions.updateOptions({ isOpen: false }));
			return;
		}

		dispatch(setLoading(true));

		const blob = await renderer.screenshot();
		const attachment = await AppApi.createAttachment({ file: blob });

		await ProjectApi.update({
			id: project.id,
			image: attachment,
		});

		const payload: SubmitPayload = {
			projectId: project.id,
		};

		window!.top!.postMessage(
			{
				title: MessageDataTitle.ADD_TO_CART,
				payload,
			},
			'*'
		);

		dispatch(setLoading(false));
		handleClose();
	};

	return (
		<Box className="creator-wrapper" component="form" onSubmit={handleSubmit}>
			<LoadingOverlay
				visible={isLoading}
				zIndex={APP_CONFIG.Z_INDEX.CREATOR_LOADING}
			/>
			<ProductPreview />
			<Aside />
			<CreatorHelpDrawer />
			<CreatorSummaryDrawer />
			<DownloadDrawer />
			<RestoreProjectModal />
		</Box>
	);
};
